import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DatePickerCustom, { DatePickerCustomDropDown } from '../../DatePickerCustom'
import { format } from 'date-fns'
import { calculateDateRange } from '../../../utils/dateUtils'

function DayFilteringWidget({ daysFilterList, setDaysFilter, daysFilter }) {
    const { t } = useTranslation()

    const handleFilterSelect = (item) => {
        if (item.code === 'custom') {
            // Custom date handling is done in CustomDateButton
            return
        }

        // For static filters, calculate and add startDate and endDate
        const { startDate, endDate } = calculateDateRange(item)
        const filterWithDates = {
            ...item,
            startDate,
            endDate,
        }
        setDaysFilter(filterWithDates)
    }

    return (
        <div className=" w-full h-[60px] flex flex-row items-center justify-between" data-tour="filter">
            <div className="flex flex-row items-center gap-[10px]">
                {daysFilterList.map((item, index) => {
                    const selected = item.code === daysFilter.code
                    if (item.code === 'custom') return <CustomDateButton selected={selected} setDaysFilter={setDaysFilter} item={item} key={index} daysFilter={daysFilter} />
                    return (
                        <button
                            className={`flex items-center justify-center rounded-full w-[130px] h-[30px] transition ${
                                selected ? 'bg-deepPurple ' : 'bg-[#FFFFFF66] border border-[#FFFFFF66] hover:border-mediumGray'
                            } `}
                            onClick={() => handleFilterSelect(item)}
                            key={index}
                        >
                            <p className={`text-[16px] leading-5 ${selected ? 'text-white' : 'text-mediumGray'} font-medium transition`}>{t(item.label)}</p>
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

const CustomDateButton = ({ selected = false, setDaysFilter, item, daysFilter }) => {
    const { t } = useTranslation()
    const [pickerOpen, setPickerOpen] = useState(false)

    // GLOBAL
    const startDate = daysFilter?.startDate ? new Date(daysFilter.startDate) : null
    const endDate = daysFilter?.endDate ? new Date(daysFilter.endDate) : null

    // LOCAL DRAFT
    const [draftStart, setDraftStart] = useState(null)
    const [draftEnd, setDraftEnd] = useState(null)

    // when opening the picker, set the draft dates to the current filter dates
    useEffect(() => {
        if (pickerOpen) {
            setDraftStart(startDate)
            setDraftEnd(endDate)
        }
    }, [pickerOpen, daysFilter])

    const handleDateSelect = (date) => {
        if (!draftStart || (draftStart && draftEnd)) {
            setDraftStart(date)
            setDraftEnd(null)
            return
        }

        if (!draftEnd) {
            if (date > draftStart) {
                setDraftEnd(date)
            } else {
                setDraftStart(date)
                setDraftEnd(null)
            }
        }
    }

    const handleOkButton = () => {
        if (!draftStart || !draftEnd) return

        setDaysFilter({
            ...item,
            code: 'custom',
            startDate: format(draftStart, 'yyyy-MM-dd'),
            endDate: format(draftEnd, 'yyyy-MM-dd'),
        })

        setPickerOpen(false)
    }

    const customDateLabel = useMemo(() => {
        if (!startDate || !endDate) return t(item.label)
        return `${format(startDate, 'dd/MM/yyyy')} - ${format(endDate, 'dd/MM/yyyy')}`
    }, [startDate, endDate, item.label, t])

    return (
        <div style={{ position: 'relative', zIndex: 2 }} onMouseLeave={() => setPickerOpen(false)}>
            <button
                className={`relative flex items-center justify-center rounded-full min-w-[130px] h-[30px] transition px-[15px] ${
                    selected || pickerOpen ? 'bg-deepPurple' : 'bg-[#FFFFFF66] border border-[#FFFFFF66]'
                }`}
                onClick={() => setPickerOpen(true)}
            >
                <p className={`text-[16px] ${selected || pickerOpen ? 'text-white' : 'text-mediumGray'}`}>{selected ? customDateLabel : t(item.label)}</p>
            </button>

            {pickerOpen && (
                <DatePickerCustomDropDown
                    showPredefinedDates={false}
                    startDate={draftStart}
                    endDate={draftEnd}
                    handleDateSelect={handleDateSelect}
                    handleOkButton={handleOkButton}
                    closePicker={() => setPickerOpen(false)}
                />
            )}
        </div>
    )
}

export default DayFilteringWidget
